import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'

import { useAppDispatch, RootState } from '../../redux/store'
import { fetchActeursByActionId } from '../../redux/actorAction/actorActionAction'
import {
  selectActeurAction,
  selectActeurActionError,
  selectActeurActionLoading,
} from '../../redux/actorAction/actorActionSelectors'
import { SymboleRasci } from '../../utils/constants'
import ActorAction from '../../components/containers/ActorAction'
import { Action } from '../../types/actionTypes'
import { selectUsername } from '../../redux/keycloak/keycloakSelectors'
import { UserRole } from '../../types/keycloak'
import { hasRole } from '../../utils/checkRole'
import { DetailButton, DetailEmptyState } from '../../components/common/detail'
import '../../components/common/detail/detail.css'

const ROLE_ORDER: SymboleRasci[] = [
  SymboleRasci.RESPONSIBLE,
  SymboleRasci.APPROVER,
  SymboleRasci.SUPPORT,
  SymboleRasci.VIEWED,
  SymboleRasci.INFORMED,
]

const ROLE_LETTER: Record<string, string> = {
  [SymboleRasci.RESPONSIBLE]: 'R',
  [SymboleRasci.APPROVER]: 'A',
  [SymboleRasci.SUPPORT]: 'S',
  [SymboleRasci.VIEWED]: 'C',
  [SymboleRasci.INFORMED]: 'I',
}

const ROLE_META: Record<
  string,
  { bg: string; color: string; soft: string }
> = {
  [SymboleRasci.RESPONSIBLE]: {
    bg: '#96271B',
    color: '#FAE6E3',
    soft: '#FAE6E3',
  },
  [SymboleRasci.APPROVER]: {
    bg: '#076B5C',
    color: '#E1F4F0',
    soft: '#E1F4F0',
  },
  [SymboleRasci.SUPPORT]: {
    bg: '#22548A',
    color: '#E6EFF8',
    soft: '#E6EFF8',
  },
  [SymboleRasci.VIEWED]: {
    bg: '#84540A',
    color: '#FAEFDA',
    soft: '#FAEFDA',
  },
  [SymboleRasci.INFORMED]: {
    bg: '#5F7484',
    color: '#EAF0F0',
    soft: '#EAF0F0',
  },
}

interface RASCIMatrixProps {
  action: Action
}

const RASCIMatrix: React.FC<RASCIMatrixProps> = ({ action }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { id } = useParams()
  const actor = useSelector(selectActeurAction)
  const loading = useSelector(selectActeurActionLoading)
  const error = useSelector(selectActeurActionError)
  const username = useSelector(selectUsername)
  const roles = useSelector((state: RootState) => state.auth.roles)

  const [openActorPopup, setOpenActorPopup] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)

  useEffect(() => {
    if (id) dispatch(fetchActeursByActionId(id))
  }, [dispatch, id])

  const canManage =
    action.CreatedBy?.toLocaleLowerCase() === username?.toLocaleLowerCase() ||
    hasRole(roles, UserRole.ADMIN) ||
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.SSI_TEAM)

  const columns = useMemo(() => {
    const map = actor?.RASCI || {}
    return ROLE_ORDER.map((role) => {
      const raw = (map as Record<string, unknown>)[role]
      const users = Array.isArray(raw)
        ? (raw as string[]).filter(Boolean)
        : []
      return { role, users }
    })
  }, [actor])

  const hasRasci = columns.some((c) => c.users.length > 0)

  const handleOpen = (edit: boolean) => {
    setIsEditMode(edit)
    setOpenActorPopup(true)
  }

  const handleClose = () => {
    setOpenActorPopup(false)
    setIsEditMode(false)
    if (id) dispatch(fetchActeursByActionId(id))
  }

  if (loading) {
    return <div className="detail-loading">{t('loading')}</div>
  }

  if (error) {
    return (
      <DetailEmptyState
        title={t('error', { defaultValue: 'Something went wrong' })}
        message={error}
      />
    )
  }

  return (
    <>
      <div className="rasci-board">
        <div className="rasci-board-head">
          <div>
            <p className="rasci-board-kicker">RASCI</p>
            <h4 className="rasci-board-title">
              {t('popups.matrixRasci.dialog.titleAssign')}
            </h4>
          </div>
          {canManage ? (
            <DetailButton
              size="sm"
              onClick={() => handleOpen(hasRasci)}
            >
              <DriveFileRenameOutlineOutlinedIcon sx={{ fontSize: 15 }} />
              {hasRasci
                ? t('buttons.edit', { defaultValue: 'Edit' })
                : t('popups.matrixRasci.assignButton')}
            </DetailButton>
          ) : null}
        </div>

        {!hasRasci ? (
          <DetailEmptyState
            title={t('missionDetailsPage.empty.title', {
              defaultValue: 'Nothing here yet',
            })}
            message={t('taskDetailsPage.empty.rasci', {
              defaultValue:
                'No RASCI roles assigned yet. Assign Responsible, Approver, Support, Consulted and Informed.',
            })}
            action={
              canManage ? (
                <DetailButton onClick={() => handleOpen(false)}>
                  {t('popups.matrixRasci.assignButton')}
                </DetailButton>
              ) : undefined
            }
          />
        ) : (
          <div className="rasci-columns" role="list">
            {columns.map(({ role, users }) => {
              const meta = ROLE_META[role] || ROLE_META[SymboleRasci.INFORMED]
              const letter = ROLE_LETTER[role] || '?'
              const label = t(`popups.matrixRasci.roles.${role}`, {
                defaultValue: role,
              })

              return (
                <article
                  key={role}
                  className="rasci-col"
                  role="listitem"
                  style={{ ['--rasci-accent' as string]: meta.bg }}
                >
                  <header className="rasci-col-head">
                    <span
                      className="rasci-letter"
                      style={{ background: meta.bg, color: '#fff' }}
                    >
                      {letter}
                    </span>
                    <div className="rasci-col-label">
                      <span className="rasci-col-name">{label}</span>
                      <span className="rasci-col-count">
                        {users.length === 0
                          ? t('taskDetailsPage.rasci.unassigned', {
                              defaultValue: 'Unassigned',
                            })
                          : t('taskDetailsPage.rasci.people', {
                              count: users.length,
                              defaultValue: `${users.length} assigned`,
                            })}
                      </span>
                    </div>
                  </header>

                  <div className="rasci-col-body">
                    {users.length === 0 ? (
                      <div className="rasci-unassigned">
                        <span className="rasci-unassigned-dot" />
                        {t('taskDetailsPage.rasci.noneYet', {
                          defaultValue: 'No one assigned',
                        })}
                      </div>
                    ) : (
                      <ul className="rasci-people">
                        {users.map((user, index) => (
                          <li key={`${role}-${index}`} className="rasci-person">
                            <span
                              className="rasci-person-avatar"
                              style={{
                                background: meta.soft,
                                color: meta.bg,
                              }}
                              aria-hidden
                            >
                              {String(user).charAt(0).toUpperCase()}
                            </span>
                            <span className="rasci-person-name">{user}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </div>

      {openActorPopup ? (
        <ActorAction
          navigateTo={false}
          actionId={action._id as string}
          isOpen={openActorPopup}
          existingRasci={actor}
          isUpdate={isEditMode}
          onClose={handleClose}
        />
      ) : null}
    </>
  )
}

export default RASCIMatrix
